/* Copyright (c) 2020-present CloudIO Inc */

import {
  StateXSetter,
  useStateXAtom,
  useStateXGetter,
  useStateXResetter,
  useStateXSetter,
} from '@cloudio-saas/statex';
import {
  Box,
  Divider,
  IconButton,
  InputBase,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { usePinned } from 'common/Context';
import { AnyItem, getManager, getName, isNull, Page } from 'componentregistry';
import { Platform } from 'componentregistry/platform/Platform';
import { activeItemSelector } from 'designer/activeItemSelector';
import { pageAtom } from 'designer/atoms';
import { useDeferredValue } from 'experimental/ExperimentalReact';
import Icon from 'icons/Icon';
import { TreeType } from 'layout/Sidebar/panels/TreeView/state';
import React, { useCallback, useEffect, useRef } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import {
  itemExpandedPageLevelAtom,
  itemFilteredAtom,
  itemFilteredPageLevelAtom,
  matchedItemsAtom,
  matchedItemsCurrentIndexAtom,
  treeViewFilterAtom,
  treeViewFilterMatchCaseAtom,
  treeViewFilterMatchWholeWordAtom,
  treeViewSearchAtom,
} from '../../atoms';
import ExpandCollapseIcon from '../ExpandCollapseIcon';
import TreeViewMenu from './TreeViewMenu';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '0px 4px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: 40,
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
    cursor: 'pointer',
  },
  divider: {
    height: 28,
    margin: 4,
  },
}));

function compare(
  value: string,
  filter: string,
  matchCase: boolean,
  matchWholeWord: boolean,
): boolean {
  if (!value) return false;
  const val = matchCase ? value : value.toLowerCase();
  return matchWholeWord ? val === filter : val.includes(filter);
}

function isMatched(
  filter: string,
  item: AnyItem,
  matchCase: boolean,
  matchWholeWord: boolean,
) {
  if (
    compare(item.itemId, filter, matchCase, matchWholeWord) ||
    compare(item.type, filter, matchCase, matchWholeWord) ||
    compare(getName(item.type), filter, matchCase, matchWholeWord)
  ) {
    return true;
  }
  const mgr = getManager(item.type);
  if (!mgr) return false;
  const itemWithDefault = itemWithDefaultValues(item);
  const found = Object.values(itemWithDefault.props).find((val) => {
    if (isNull(val)) {
      return false;
    }
    const v = val as string | Record<string, unknown>;
    switch (typeof v) {
      case 'string':
        if (compare(v, filter, matchCase, matchWholeWord)) {
          return true;
        }
        break;
      case 'object':
        {
          const { value, iconName } = v;
          if (
            typeof value === 'string' &&
            compare(value, filter, matchCase, matchWholeWord)
          ) {
            return true;
          }
          if (
            typeof iconName === 'string' &&
            compare(iconName, filter, matchCase, matchWholeWord)
          ) {
            return true;
          }
        }
        break;
      default:
    }
    return false;
  });
  return !!found;
}

function searchAndCollect(props: {
  set: StateXSetter;
  appUid: string;
  pageId: string;
  treename: string;
  page: Page;
  filter: string;
  itemId: string;
  collect: string[];
  matchCase: boolean;
  matchWholeWord: boolean;
}) {
  const {
    set,
    appUid,
    pageId,
    treename,
    page,
    filter,
    itemId,
    collect,
    matchCase,
    matchWholeWord,
  } = props;
  const item = page[itemId];
  let matched = false;
  if (isMatched(filter, item, matchCase, matchWholeWord)) {
    matched = true;
    collect.push(item.itemId);
    set(itemFilteredAtom, true, {
      params: { appUid, pageId, itemId: item.itemId, treename },
    });
  }
  item.children.forEach((childId) => {
    searchAndCollect({
      set,
      appUid,
      pageId,
      treename,
      page,
      filter,
      itemId: childId,
      collect,
      matchCase,
      matchWholeWord,
    });
  });
  return matched;
}

type TreeViewHeaderProps = {
  treename: TreeType;
  className?: string;
  platform: Platform;
  appUid: string;
  pageId: string;
};

export default function TreeViewHeader({
  treename,
  className,
  platform,
  pageId,
  appUid,
}: TreeViewHeaderProps): React.ReactElement {
  const classes = useStyles();
  const pinned = usePinned();
  const ref = useRef<HTMLInputElement>(null);
  const [filteredIds, setFilteredIds] = useStateXAtom(matchedItemsAtom, {
    params: { appUid, pageId, treename },
  });
  const [index, setIndex] = useStateXAtom(matchedItemsCurrentIndexAtom, {
    onChange: ({ set, value }) => {
      if (filteredIds.length) {
        set(
          activeItemSelector,
          {
            appUid,
            pageId,
            itemId: filteredIds[value],
            expand: false,
            treename,
          },
          { params: { appUid, treename, pageId }, props: platform },
        );
      }
    },
    params: { appUid, pageId, treename },
  });
  const set = useStateXSetter();
  const reset = useStateXResetter();
  const get = useStateXGetter();
  const [filter, setFilter] = useStateXAtom(treeViewFilterAtom, {
    params: { treename },
  });

  const doFilter = useCallback(() => {
    const _search = get(treeViewSearchAtom, {
      params: { treename },
    });
    const _matchCase = get(treeViewFilterMatchCaseAtom, {
      params: { treename },
    });
    const _matchWholeWord = get(treeViewFilterMatchWholeWordAtom, {
      params: { treename },
    });
    reset(matchedItemsAtom, {
      params: { appUid, pageId, treename },
    });
    reset(itemExpandedPageLevelAtom, {
      params: { appUid, pageId, treename },
    });
    reset(itemFilteredPageLevelAtom, {
      params: { appUid, pageId, treename },
    });
    if (_search) {
      const page: Page | undefined = get(pageAtom, {
        params: { appUid, pageId },
      });
      if (page) {
        const collect: string[] = [];
        searchAndCollect({
          set,
          appUid,
          pageId,
          treename,
          page,
          filter: _matchCase ? _search : _search.toLowerCase(),
          itemId: 'root',
          collect,
          matchCase: _matchCase,
          matchWholeWord: _matchWholeWord,
        });
        if (collect.length) {
          setFilteredIds(collect);
          setIndex(0);
          set(
            activeItemSelector,
            { appUid, pageId, itemId: collect[0], expand: true, treename },
            { params: { appUid, treename, pageId }, props: platform },
          );
        }
      }
    } else {
      set(
        activeItemSelector,
        { appUid, pageId, itemId: 'root', expand: true, treename },
        { params: { appUid, treename, pageId }, props: platform },
      );
    }
  }, [
    appUid,
    get,
    pageId,
    platform,
    reset,
    set,
    setFilteredIds,
    setIndex,
    treename,
  ]);

  const [matchCase, setMatchCase] = useStateXAtom(treeViewFilterMatchCaseAtom, {
    params: { treename },
    onChange: doFilter,
  });
  const [matchWholeWord, setMatchWholeWord] = useStateXAtom(
    treeViewFilterMatchWholeWordAtom,
    {
      params: { treename },
      onChange: doFilter,
    },
  );

  const [, setSearch] = useStateXAtom(treeViewSearchAtom, {
    params: { treename },
    onChange: doFilter,
  });

  const deferredFilter = useDeferredValue(filter);
  useEffect(() => {
    setSearch(deferredFilter);
  }, [deferredFilter, setSearch]);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFilter(e.target.value);
  }

  return (
    <div className={clsx(classes.root, className)}>
      <IconButton
        onClick={() => ref.current?.focus()}
        className={classes.iconButton}
        aria-label="Filter"
      >
        <span aria-hidden="false" style={{ display: 'none' }}>
          Filter
        </span>
        <Icon iconName="search" />
      </IconButton>
      <InputBase
        inputRef={ref}
        className={classes.input}
        placeholder="Search Page Tree"
        inputProps={{
          'aria-label': 'search page tree',
          'data-testid': 'search',
        }}
        value={filter ?? ''}
        onChange={onChange}
        autoFocus
      />
      {filter ? (
        <Typography variant="caption" sx={{ cursor: 'default' }}>
          {(index === -1 ? 0 : index) + (filteredIds.length ? 1 : 0)} /{' '}
          {filteredIds.length}
        </Typography>
      ) : null}

      {filteredIds.length ? (
        <IconButton
          data-testid="tree-prev"
          disabled={index === 0}
          type="button"
          className={classes.iconButton}
          aria-label="previous"
          size="small"
          onClick={() => {
            setIndex(Math.max(0, index - 1));
          }}
        >
          <Icon iconName="chevron-left" />
        </IconButton>
      ) : null}
      {filteredIds.length ? (
        <IconButton
          data-testid="tree-next"
          disabled={filteredIds.length - 1 === index}
          type="button"
          className={classes.iconButton}
          aria-label="next"
          size="small"
          onClick={() => {
            setIndex(Math.min(filteredIds.length - 1, index + 1));
          }}
        >
          <Icon iconName="chevron-right" />
        </IconButton>
      ) : null}
      <Box
        data-testid="tree-match-case"
        onClick={() => {
          setMatchCase(!matchCase);
        }}
        data-tip="Match Case"
        sx={{
          padding: '4px',
          bgcolor: matchCase ? 'primary.main' : undefined,
          display: 'flex',
          alignItems: 'center',
          marginLeft: '4px',
          cursor: 'pointer',
          borderRadius: '4px',
        }}
      >
        <Icon
          iconName="font-case"
          parentColor={matchCase ? 'primary' : undefined}
        />
      </Box>
      <Box
        data-testid="tree-match-whole-word"
        onClick={() => {
          setMatchWholeWord(!matchWholeWord);
        }}
        data-tip="Match Whole Word"
        sx={{
          padding: '4px',
          bgcolor: matchWholeWord ? 'primary.main' : undefined,
          display: 'flex',
          alignItems: 'center',
          marginLeft: '4px',
          cursor: 'pointer',
          borderRadius: '4px',
        }}
      >
        <Icon
          iconName="text-width"
          parentColor={matchWholeWord ? 'primary' : undefined}
        />
      </Box>
      <Divider className={classes.divider} orientation="vertical" />
      <TreeViewMenu appUid={appUid} pageId={pageId} treename={treename} />
      {treename === 'designer' ? <ExpandCollapseIcon pinned={pinned} /> : null}
    </div>
  );
}
